// Sample data loader for the Studio showcase
import type { Video, Segment, Transcript, TranscriptSentence } from '../types'

export interface SampleMetadata {
  id: string
  title: string
  description: string
  duration: number
  thumbnail: string
  source: string
  category: string
  featured: boolean
}

export interface SampleIndex {
  samples: SampleMetadata[]
  categories: string[]
  version: string
}

export interface SampleData {
  video: Video
  transcript: Transcript
  segments: Segment[]
}

function joinBase(path: string): string {
  const base = (import.meta as any).env?.BASE_URL || '/'
  const normalizedBase = String(base).endsWith('/') ? String(base) : `${String(base)}/`
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path
  return `${normalizedBase}${normalizedPath}`
}

const SAMPLES_BASE_URL = joinBase('samples')

/**
 * Load the sample index listing all available samples
 */
export async function loadSampleIndex(): Promise<SampleIndex> {
  const response = await fetch(`${SAMPLES_BASE_URL}/index.json`)
  if (!response.ok) {
    throw new Error('Failed to load sample index')
  }
  return response.json()
}

/**
 * Load complete sample data (metadata, transcript, segments)
 */
export async function loadSampleData(sampleId: string): Promise<SampleData> {
  const basePath = `${SAMPLES_BASE_URL}/${sampleId}`
  
  // Load all data in parallel
  const [metadataRes, transcriptRes, segmentsRes] = await Promise.all([
    fetch(`${basePath}/metadata.json`),
    fetch(`${basePath}/transcript.json`),
    fetch(`${basePath}/segments.json`)
  ])
  
  if (!metadataRes.ok) throw new Error('Failed to load sample metadata')
  if (!transcriptRes.ok) throw new Error('Failed to load sample transcript')
  if (!segmentsRes.ok) throw new Error('Failed to load sample segments')
  
  const metadata = await metadataRes.json()
  const transcriptData = await transcriptRes.json()
  const segmentsData = await segmentsRes.json()
  
  // Convert to Video format
  const video: Video = {
    id: metadata.id,
    url: metadata.source_url
      ? (metadata.source_url.startsWith('s3://') || metadata.source_url.startsWith('minio://') ? metadata.source_url : joinBase(metadata.source_url))
      : '',
    title: metadata.title,
    duration: metadata.duration,
    thumbnail: metadata.thumbnail
      ? (metadata.thumbnail.startsWith('s3://') || metadata.thumbnail.startsWith('minio://') ? metadata.thumbnail : joinBase(metadata.thumbnail))
      : metadata.thumbnail,
    platform: metadata.platform,
    original: metadata.original && metadata.original.url ? metadata.original : undefined,
  }
  
  // Convert to Transcript format
  const sentences: TranscriptSentence[] = transcriptData.sentences.map((s: any) => ({
    id: s.id,
    text: s.text,
    start: s.start,
    end: s.end,
    words: s.words,
    speaker: s.speaker,
    isSelected: false
  }))
  
  const transcript: Transcript = {
    video_id: transcriptData.video_id,
    language: transcriptData.language,
    words: transcriptData.sentences.flatMap((s: any) => s.words),
    sentences: sentences,
    duration: transcriptData.duration,
    created_at: transcriptData.created_at
  }
  
  // Convert segments
  const segments: Segment[] = segmentsData.segments.map((seg: any) => ({
    id: seg.id,
    video_id: seg.video_id,
    start: seg.start,
    end: seg.end,
    text: seg.text,
    speaker: seg.speaker,
    confidence: seg.confidence,
    color: seg.color,
    highlight_score: seg.highlight_score,
    is_manual: seg.is_manual,
    sentences: seg.sentences
  }))
  
  return { video, transcript, segments }
}

/**
 * Get featured samples for the showcase
 */
export async function getFeaturedSamples(): Promise<SampleMetadata[]> {
  const index = await loadSampleIndex()
  return index.samples.filter(s => s.featured)
}

/**
 * Get samples by category
 */
export async function getSamplesByCategory(category: string): Promise<SampleMetadata[]> {
  const index = await loadSampleIndex()
  return index.samples.filter(s => s.category === category)
}
